import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CircularProgress } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

import { theme } from '@verantos-dev/shared-styles';
import { borderRadius, fontSize } from '@verantos-dev/shared-ui-components/tokens';

const getBorderColor = (
  type: DialogMessageBoxProps['type'],
  variant: DialogMessageBoxProps['variant'],
  muiTheme: Theme
) => {
  if (variant === 'outlined') return muiTheme.palette.neutral[100];
  if (type === 'error') return muiTheme.palette.error.main;
  if (type === 'warning') return muiTheme.palette.warning.main;
  if (type === 'info' || type === 'loading') return muiTheme.palette.neutral[100];
  return muiTheme.palette.success.main;
};

const getBackgroundColor = (
  type: DialogMessageBoxProps['type'],
  variant: DialogMessageBoxProps['variant'],
  muiTheme: Theme
) => {
  if (variant === 'outlined') return muiTheme.palette.common.white;
  // TODO: no VDS token for this value — needs designer input (#fdf2f1 error light bg)
  if (type === 'error') return '#fdf2f1';
  // TODO: no VDS token for this value — needs designer input (#fef8e3 warning light bg)
  if (type === 'warning') return '#fef8e3';
  if (type === 'info' || type === 'loading') return muiTheme.palette.common.white;
  // TODO: no VDS token for this value — needs designer input (#e4fae7 success light bg)
  return '#e4fae7';
};

const MessageBox = styled('div')<{
  type: DialogMessageBoxProps['type'];
  variant?: DialogMessageBoxProps['variant'];
}>(({ type, variant, theme: muiTheme }) => ({
  width: '100%',
  minHeight: '80px',
  border: '1px solid',
  borderRadius: borderRadius.md,
  display: 'flex',
  flexDirection: 'row',
  borderColor: getBorderColor(type, variant, muiTheme),
  backgroundColor: getBackgroundColor(type, variant, muiTheme),
}));

const IconArea = styled('div')({
  width: '56px',
  height: '100%',
  paddingTop: '18px',
  display: 'flex',
  justifyContent: 'center',
});

const TextArea = styled('div')({
  width: 'calc(100% - 56px)',
  fontSize: fontSize[16],
  // TODO: no VDS token for this value — needs designer input (#51555e)
  color: '#51555e',
  lineHeight: '24px',
  padding: '19px 56px 16px 0',
});

const Title = styled('div')(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const Text = styled('div')({
  display: 'flex',
  alignItems: 'center',
  overflowWrap: 'anywhere',
});

export type DialogMessageBoxProps = {
  /**
   * @prop {string} id - id of the entry, used to create testId
   */
  id: string;

  /**
   * @prop {'filled' | 'outlined'} variant - backgroud color of the message box
   * @default 'filled'
   */
  variant?: 'filled' | 'outlined';

  /**
   * @prop {'error' | 'success'} messageBoxType - type of the message box - also used to display the message box and correct icon
   */
  type: 'error' | 'warning' | 'success' | 'info' | 'loading';

  /**
   * @prop {string} messageBoxTitle - title of the message box
   */
  title: string;

  /**
   * @prop {React.ReactNode} messageBoxText - text of the message box
   */
  text?: React.ReactNode;

  /**
   * @prop {React.ReactNode} - The component to render extra information when the dialog is submitted
   * @description This is used to add extra information to the dialog after the data is submitted successfully along with success message
   */
  renderSubmitted?: React.ReactNode;
};

export const DialogMessageBox = ({
  id,
  title,
  type,
  variant = 'filled',
  text,
  renderSubmitted,
}: DialogMessageBoxProps) => {
  const InfoIcon = ({ color }: { color: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
        fill={color}
      />
    </svg>
  );

  return (
    <MessageBox data-testid={`dialog-${id}-messageBox`} type={type} variant={variant}>
      <IconArea data-testid={`dialog-${id}-messageBox-iconArea`}>
        {type === 'error' && (
          <CancelIcon sx={{ width: '24px', height: '24px', color: theme.palette.error.main }} />
        )}
        {type === 'warning' && <InfoIcon color={theme.palette.warning.main} />}
        {type === 'success' && (
          <CheckCircleIcon
            data-testid={`dialog-${id}-messageBox-iconArea-icon`}
            // TODO: no VDS token for this value — needs designer input (#4caf50 success icon)
            sx={{ width: '24px', height: '24px', color: '#4caf50' }}
          />
        )}
        {type === 'info' && <InfoIcon color={theme.palette.info.main} />}
        {type === 'loading' && <CircularProgress size="24px" />}
      </IconArea>
      <TextArea data-testid={`dialog-${id}-messageBox-textArea`}>
        <Title data-testid={`dialog-${id}-messageBox-textArea-title`}>{title}</Title>
        {text && <Text data-testid={`dialog-${id}-messageBox-textArea-text`}>{text}</Text>}
        {renderSubmitted}
      </TextArea>
    </MessageBox>
  );
};
